import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useQueryParams from '../../../hooks/useQueryParams';
import { setUserCurrentCompanyId } from '../../../redux/actions/userManagementActions';
import { api } from '../../../redux/rtk/api';
import {
  invalidateQuery,
  useGetVehicleOrdersQuery,
} from '../../../redux/rtk/orders.services';
import {
  currentUserCompanyId,
  selectUserCompaniesWithContracts,
} from '../../../redux/selectors/userDataSelectors';
import { ApprovalsContext } from '../Approvals.provider';

export const getBody = (pageNumber, searchQuery, compnayId) => ({
  pageSize: 25,
  pageNumber,
  order: 'desc',
  orderBy: 'date',
  filters: [],
  groupedFilters: {
    AND: [
      {
        AND: [
          {
            column: 'dealershipId',
            value: compnayId,
            operator: 'eq',
          },
          {
            column: 'requiresApproval',
            value: true,
            operator: 'eq',
          },
        ],
      },
    ],
  },
  searchQuery,
});

export default function useVehicleOrdersForApproval() {
  const { pageNumber, searchQuery, setPageNumber, setSearchQuery } =
    useContext(ApprovalsContext);
  const companyId = useSelector(currentUserCompanyId);
  const dispatch = useDispatch();
  const params = useQueryParams();
  const companies = useSelector(selectUserCompaniesWithContracts);
  const body = getBody(pageNumber, searchQuery, companyId);
  const [skip, setSkip] = useState(params.has('companyId'));
  const {
    isLoading: isLoadingData,
    isFetching: isFetchingData,
    data,
  } = useGetVehicleOrdersQuery(body, { skip });

  const isLoading = isLoadingData || isFetchingData;
  const vehicleOrders =
    isLoading && pageNumber === 1 ? [] : data?.vehicleOrders;
  const hasNextPage = data?.pagination.pageNumber < data?.pagination.totalPages;
  const isEmpty = !isLoading && !data?.vehicleOrders.length;
  const totalResults = data?.pagination.totalResults;

  const loadMore = () => {
    hasNextPage && setPageNumber(pageNumber + 1);
  };

  const refresh = () => {
    setPageNumber(1);
    dispatch(invalidateQuery(body));
  };

  useEffect(() => {
    if (!companies.length) return;
    if (!params.has('companyId')) return;

    const newCompanyId = params.get('companyId');
    if (companyId === newCompanyId) {
      setSkip(false);
      return;
    }

    if (companies.some(company => company.companyId === newCompanyId)) {
      dispatch(setUserCurrentCompanyId(newCompanyId));
      dispatch(api.util.invalidateTags('CurrentCompany'));
      return;
    }

    setSkip(false);
  }, [companyId, companies, params, dispatch]);

  return {
    isLoading,
    vehicleOrders,
    hasNextPage,
    isEmpty,
    loadMore,
    totalResults,
    refresh,
    setSearchQuery,
  };
}
